import React, { useContext, useEffect, useState } from "react";
import { Select } from "antd";
import { useNotification } from "../../NotificationContext";
import { getEpisodeType, saveEpisodeType, updateEpisodeType } from "../../apis/episodeType";
import { LanguageContext } from "../../providers/LanguageProvider";

const FIELD_TYPES = [
  { value: "text",     label: "Text" },
  { value: "number",   label: "Number" },
  { value: "date",     label: "Date" },
  { value: "textarea", label: "Long text" },
  { value: "select",   label: "Dropdown" },
  { value: "boolean",  label: "Yes / No" },
  { value: "lmp",             label: "LMP (gestational age)" },
  { value: "gplad",           label: "GPLAD (obstetric score)" },
  { value: "edd",             label: "EDD (due date + USG override)" },
  { value: "history_table",   label: "Previous pregnancy history table" },
];

const toKey = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

interface Field {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options: string[];
}

const emptyField = (): Field => ({
  key: "",
  label: "",
  type: "text",
  required: false,
  options: [],
});

const GENDER_RESTRICTION_OPTIONS = [
  { value: "",       label: "No restriction" },
  { value: "female", label: "Female patients only" },
  { value: "male",   label: "Male patients only" },
];

const AddEpisodeType: React.FC<{ closeModal: () => void; editableId: any }> = ({
  closeModal,
  editableId,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [genderRestriction, setGenderRestriction] = useState("");
  const [fields, setFields] = useState<Field[]>([]);
  const [errors, setErrors] = useState<any>({});
  const { showNotification } = useNotification();
  const { translate: t } = useContext(LanguageContext);

  useEffect(() => {
    if (editableId) {
      getEpisodeType(editableId).then((res: any) => {
        const data = res.data?.data || res.data;
        setName(data?.name || "");
        setDescription(data?.description || "");
        setGenderRestriction(data?.genderRestriction || "");
        const raw: any[] = data?.fields || [];
        setFields(
          raw.map((f: any) => ({
            key: f.key || toKey(f.label || ""),
            label: f.label || f.key || "",
            type: f.type || "text",
            required: !!f.required,
            options: Array.isArray(f.options) ? f.options : [],
          }))
        );
      });
    }
  }, [editableId]);

  const addField = () => setFields((prev) => [...prev, emptyField()]);

  const removeField = (idx: number) =>
    setFields((prev) => prev.filter((_, i) => i !== idx));

  const updateField = (idx: number, patch: Partial<Field>) => {
    setFields((prev) =>
      prev.map((f, i) => {
        if (i !== idx) return f;
        const updated = { ...f, ...patch };
        // Auto-derive key from label unless manually changed
        if (patch.label !== undefined && patch.key === undefined) {
          updated.key = toKey(patch.label);
        }
        return updated;
      })
    );
  };

  const handleSave = () => {
    const errs: any = {};
    if (!name.trim()) errs.name = "Name is required";
    fields.forEach((f, i) => {
      if (!f.label.trim()) errs[`field_${i}_label`] = "Required";
      if (f.type === "select" && f.options.length === 0)
        errs[`field_${i}_options`] = "Add at least one option";
    });
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});

    const payload = { name, description, genderRestriction: genderRestriction || null, fields };
    const action = editableId
      ? updateEpisodeType({ id: editableId, ...payload })
      : saveEpisodeType(payload);

    action
      .then(() => {
        showNotification(
          "success",
          editableId ? "Episode type updated" : "Episode type created",
          "",
          "topRight"
        );
        closeModal();
      })
      .catch((err: any) => {
        showNotification(
          "error",
          err?.response?.data?.message || "Failed to save",
          "",
          "topRight"
        );
      });
  };

  return (
    <div className="space-y-5">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-900">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
          placeholder="e.g. Pregnancy"
        />
        {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-900">Description</label>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
        />
      </div>

      {/* Gender restriction */}
      <div>
        <label className="block text-sm font-medium text-gray-900">Gender Restriction</label>
        <p className="text-xs text-gray-400 mb-1">Limit this episode type to patients of a specific gender</p>
        <Select
          value={genderRestriction || ""}
          onChange={(val) => setGenderRestriction(val ?? "")}
          options={GENDER_RESTRICTION_OPTIONS}
          className="w-full"
        />
      </div>

      {/* Intake fields */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm font-medium text-gray-900">Intake Fields</p>
            <p className="text-xs text-gray-400">
              Fields the doctor fills when creating an episode of this type
            </p>
          </div>
          <button
            type="button"
            onClick={addField}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Field
          </button>
        </div>

        {fields.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-8 text-center">
            <p className="text-sm text-gray-400">No fields yet</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Click "Add Field" to define what data to collect
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map((field, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2"
              >
                <div className="grid grid-cols-12 gap-2 items-start">
                  {/* Label */}
                  <div className="col-span-5">
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      Label
                    </label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(idx, { label: e.target.value })}
                      placeholder="e.g. LMP Date"
                      className={`w-full rounded border py-1 px-2 text-sm focus:outline-none focus:border-indigo-400 ${
                        errors[`field_${idx}_label`] ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {field.key && (
                      <p className="mt-0.5 text-[10px] text-gray-400">key: {field.key}</p>
                    )}
                  </div>

                  {/* Type */}
                  <div className="col-span-4">
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      Type
                    </label>
                    <Select
                      value={field.type}
                      onChange={(val) => updateField(idx, { type: val, options: [] })}
                      options={FIELD_TYPES}
                      size="small"
                      className="w-full"
                    />
                  </div>

                  {/* Required */}
                  <div className="col-span-2">
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      Required
                    </label>
                    <button
                      type="button"
                      onClick={() => updateField(idx, { required: !field.required })}
                      className={`w-10 h-5 rounded-full transition-colors ${
                        field.required ? "bg-indigo-600" : "bg-gray-300"
                      } relative`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          field.required ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </div>

                  {/* Delete */}
                  <div className="col-span-1 flex items-end justify-end pb-0.5">
                    <button
                      type="button"
                      onClick={() => removeField(idx)}
                      className="text-gray-300 hover:text-red-500 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Options — only for select */}
                {field.type === "select" && (
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      Options <span className="font-normal normal-case">(comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={field.options.join(", ")}
                      onChange={(e) =>
                        updateField(idx, {
                          options: e.target.value
                            .split(",")
                            .map((o) => o.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="e.g. Yes, No, Not applicable"
                      className={`w-full rounded border py-1 px-2 text-sm focus:outline-none focus:border-indigo-400 ${
                        errors[`field_${idx}_options`] ? "border-red-400" : "border-gray-300"
                      }`}
                    />
                    {errors[`field_${idx}_options`] && (
                      <p className="mt-0.5 text-xs text-red-500">{errors[`field_${idx}_options`]}</p>
                    )}
                    {field.options.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {field.options.map((opt) => (
                          <span key={opt} className="inline-flex items-center rounded px-1.5 py-0.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {opt}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
        <button
          onClick={handleSave}
          type="button"
          className="px-4 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          {editableId ? t("update") : t("save")}
        </button>
        <button
          onClick={closeModal}
          type="button"
          className="text-sm font-semibold text-gray-600 hover:text-gray-900"
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
};

export default AddEpisodeType;
